// src/components/ViewScaffold.jsx
//
// CONTEXT-BANDS-S1 — Shared legend/key row for visualization ("viz") views
// only: Leases Timeline and Market Plan Timeline. List/table/matrix views
// never mount this component — gating happens at the call site (the parent
// decides whether to render <ViewScaffold> at all for the active view).
//
// Layout: legend chips (left) → ⓘ info popover (inline, click-to-toggle) →
// optional right-aligned `controls` slot (e.g. the Timeline horizon toggle).
//
// This replaces the bespoke TimelineKeyBar (Leases Timeline) and the
// TimelineLegend/HeatmapLegend + RANGE_OPTIONS row (Market Plan Timeline)
// with one shared implementation.

import React, { useState, useRef, useEffect } from "react";
import { T } from "../app/tokens.js";
import { Tooltip } from "./Tooltip.jsx";

function LegendSwatch({ indicator }) {
  if (!indicator) return null;
  if (indicator.type === "line") {
    return (
      <svg
        aria-hidden="true"
        width={18}
        height={4}
        style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
      >
        <line
          x1={0}
          y1={2}
          x2={18}
          y2={2}
          stroke={indicator.color}
          strokeWidth={2.5}
          strokeDasharray={indicator.dashed ? "5 3" : undefined}
        />
      </svg>
    );
  }
  if (indicator.shape === "square") {
    return (
      <span
        aria-hidden="true"
        style={{
          display:      "inline-block",
          width:        12,
          height:       12,
          borderRadius: 3,
          background:   indicator.color,
          border:       `1.5px solid ${indicator.border ?? indicator.color}`,
          flexShrink:   0,
        }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        display:      "inline-block",
        width:        10,
        height:       10,
        borderRadius: "50%",
        background:   indicator.color,
        flexShrink:   0,
      }}
    />
  );
}

/**
 * @param {object}   props
 * @param {Array}    [props.legendItems] Array of { label, indicator: {color, type?, dashed?, shape?, border?} }.
 * @param {ReactNode} [props.infoText]   Popover body content shown via the ⓘ toggle.
 * @param {ReactNode} [props.controls]   Optional right-aligned control slot (e.g. horizon toggle).
 * @param {string}   [props.testid]      data-testid root override (default "view-scaffold").
 * @param {string}   [props.surface]     Visual surface treatment: "pill" (default, blue-tinted
 *                                       alert-style pill) or "neutral" (white/transparent chrome,
 *                                       no border, matching the Leases Timeline legend).
 */
export function ViewScaffold({ legendItems = [], infoText = null, controls = null, testid = "view-scaffold", surface = "pill" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isNeutral = surface === "neutral";

  useEffect(() => {
    if (!open) return undefined;
    function handleClickAway(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [open]);

  return (
    <div
      data-testid={testid}
      style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        gap:            12,
        flexWrap:       "wrap",
        padding:        isNeutral ? "0 0 10px" : "8px 14px",
        background:     isNeutral ? "transparent" : T.blueSoft,
        border:         isNeutral ? "none" : `1px solid ${T.blueSoftBorder}`,
        borderRadius:   isNeutral ? 0 : 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        {legendItems.map((item, i) => (
          <span
            key={item.key ?? i}
            data-testid="view-scaffold-legend-item"
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: T.fsXs5, color: T.mid, fontWeight: T.fwSemibold }}
          >
            <LegendSwatch indicator={item.indicator} />
            {item.label}
          </span>
        ))}

        {infoText && (
          <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
            <button
              type="button"
              data-testid="view-scaffold-info-btn"
              aria-label="More information"
              onClick={() => setOpen((v) => !v)}
              style={{
                border:       `1px solid ${T.border}`,
                background:   T.white,
                borderRadius: "50%",
                width:        18,
                height:       18,
                lineHeight:   "16px",
                fontSize:     T.fsXs,
                fontWeight:   T.fwBold,
                color:        T.mid,
                cursor:       "pointer",
                padding:      0,
              }}
            >
              ⓘ
            </button>
            {open && (
              <Tooltip
                data-testid="view-scaffold-info-popover"
                style={{
                  top:        "calc(100% + 6px)",
                  left:       0,
                  zIndex:     20,
                  width:      240,
                  whiteSpace: "normal",
                  padding:    "10px 12px",
                  // click-toggle popover — allow pointer events so it stays open on hover
                  pointerEvents: "auto",
                }}
              >
                {infoText}
              </Tooltip>
            )}
          </span>
        )}
      </div>

      {controls && (
        <div data-testid="view-scaffold-controls" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {controls}
        </div>
      )}
    </div>
  );
}
